import React, { useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Badge } from '@components/base/badge';
import { XMarkIcon as XMarkIconSmall } from '@heroicons/react/16/solid';
import { ISettingOption } from '@biolibtech/biolib-js';
import { useGetUserGroupsQuery } from '../../../../../apiHooks/userApi';
import config from '../../../../../config';

interface IProps {
    selectedOptions: ISettingOption[];
    setSelectedOptions: (options: ISettingOption[]) => void;
}

export default function UserGroupsMultiselect(props: IProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { selectedOptions, setSelectedOptions } = props;
    const [query, setQuery] = useState('');

    const { data: groupsData, isLoading } = useGetUserGroupsQuery({ pageSize: 1000 });

    const allOptions: ISettingOption[] = React.useMemo(() => {
        if (!groupsData?.results) {
            return [];
        }
        return groupsData.results.map((group) => {
            const displayName = `${config.resourcePrefix}${group.account.handle}`;
            return {
                key: displayName,
                value: displayName,
            };
        });
    }, [groupsData]);

    const filteredOptions: ISettingOption[] =
        query === ''
            ? allOptions
            : allOptions.filter((option) => option.key.toLowerCase().includes(query.toLowerCase()));

    function removeOption(option: ISettingOption) {
        setSelectedOptions(selectedOptions.filter(({ key }) => key !== option.key));
    }

    function openDropdown() {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    if (isLoading) {
        return <div className='flex items-center justify-center p-4 text-gray-500'>Loading groups...</div>;
    }

    return (
        <Combobox
            immediate={true}
            multiple={true}
            as='div'
            value={selectedOptions}
            onChange={setSelectedOptions}
            onClose={() => setQuery('')}
            className='flex flex-col space-y-2 w-full'
            virtual={{
                // @ts-ignore
                options: filteredOptions,
            }}
        >
            <div className='flex flex-row w-full justify-between rounded-md bg-white text-gray-900 shadow-sm border border-gray-300'>
                <div className='flex-auto flex flex-wrap flex-row p-0.5 items-center'>
                    {selectedOptions.map((option) => (
                        <Badge key={option.key} className='whitespace-nowrap w-fit h-fit p-1 m-0.5'>
                            <span className='cursor-pointer' onClick={openDropdown}>
                                {option.key}
                            </span>
                            <XMarkIconSmall
                                className='h-4 w-4 cursor-pointer hover:text-red-600'
                                onClick={() => removeOption(option)}
                            />
                        </Badge>
                    ))}

                    <ComboboxInput
                        ref={inputRef}
                        className='grow border-none min-w-10 h-7 focus:ring-none focus:ring-inset focus:ring-cyan-800 rounded-md m-0.5'
                        onChange={(event) => setQuery(event.target.value)}
                        onBlur={() => setQuery('')}
                        placeholder='Click to add...'
                    />
                </div>

                <div className='flex-none flex items-center w-10 border-l border-gray-300 inset-y-0 bottom-0 right-0 rounded-r-md focus:outline-none p-1'>
                    <div
                        className='m-auto p-1 cursor-pointer hover:bg-gray-100 rounded-md'
                        onClick={() => setSelectedOptions([])}
                    >
                        <XMarkIcon className='h-5 w-5 text-gray-400 m-auto' aria-hidden='true' />
                    </div>
                </div>
            </div>

            <div className='w-full relative'>
                {!query || filteredOptions.length > 0 ? null : (
                    <div className='absolute border z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
                        <p className='p-3 text-gray-500'>No results</p>
                    </div>
                )}

                <ComboboxOptions
                    modal={false}
                    className='absolute border empty:invisible z-20 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'
                >
                    {({ option }) => (
                        <ComboboxOption
                            key={option.key}
                            value={option}
                            className='w-full border-b border-gray-50 group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-cyan-800 data-[focus]:text-white'
                        >
                            <span className='absolute inset-y-0 left-0 flex items-center px-2 text-cyan-800 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden'>
                                <CheckIcon aria-hidden='true' className='h-5 w-5' />
                            </span>
                            <span className='ml-6'>{option.key}</span>
                        </ComboboxOption>
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
}
